import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  BedDouble,
  CalendarDays,
  KeyRound,
  Loader2,
  LogOut,
  Mail,
  Phone,
  PlaneLanding,
  PlaneTakeoff,
  RefreshCw,
  Trash2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { OwnerHub } from "@/components/owner/OwnerHub";
import { ChangesPopup } from "@/components/owner/ChangesPopup";

const STATUS_STYLES = {
  new: "border-amber-400/60 bg-amber-500/10 text-amber-200",
  contacted: "border-sky-400/50 bg-sky-500/10 text-sky-200",
  confirmed: "border-emerald-400/50 bg-emerald-500/10 text-emerald-200",
  archived: "border-neutral-600 bg-neutral-800/40 text-neutral-400",
};

const STATUS_ACTIONS = {
  new: ["contacted", "confirmed", "archived"],
  contacted: ["confirmed", "archived"],
  confirmed: ["archived"],
  archived: ["new"],
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const RequestCard = ({ req, onStatus, onDelete }) => {
  const acc = req.accommodation || {};
  return (
    <motion.div
      layout
      data-testid={`request-card-${req.id.slice(0, 8)}`}
      className="border border-amber-500/15 bg-[#14141a] p-6"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-serif text-2xl font-light text-neutral-50">{req.name}</h3>
          <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            Ref {req.id.slice(0, 8)} · {formatDate(req.created_at)}
          </p>
        </div>
        <span
          data-testid={`request-status-${req.id.slice(0, 8)}`}
          className={`border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] ${STATUS_STYLES[req.status] || STATUS_STYLES.new}`}
        >
          {req.status}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-300">
        <a href={`mailto:${req.email}`} className="flex items-center gap-2 transition-colors hover:text-amber-200">
          <Mail className="h-3.5 w-3.5 text-amber-400/70" /> {req.email}
        </a>
        {req.phone && (
          <span className="flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-amber-400/70" /> {req.phone}
          </span>
        )}
      </div>

      <div className="mt-5 grid gap-3 border-t border-amber-500/10 pt-5 text-sm text-neutral-300 sm:grid-cols-3">
        <span className="flex items-center gap-2">
          <PlaneLanding className="h-4 w-4 shrink-0 text-amber-400/70" />
          {req.arrival_date || "—"} {req.arrival_flight ? `· ${req.arrival_flight}` : ""}
        </span>
        <span className="flex items-center gap-2">
          <PlaneTakeoff className="h-4 w-4 shrink-0 text-amber-400/70" />
          {req.departure_date || "—"} {req.departure_flight ? `· ${req.departure_flight}` : ""}
        </span>
        <span className="flex items-center gap-2">
          <Users className="h-4 w-4 shrink-0 text-amber-400/70" />
          Party of {req.party_size || "—"}
        </span>
      </div>

      {req.services?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {req.services.map((s) => (
            <span key={s} className="border border-amber-500/25 bg-amber-500/5 px-2.5 py-1 text-xs text-amber-100/90">
              {s}
            </span>
          ))}
        </div>
      )}

      {acc.already_booked && (
        <div className="mt-4 border border-amber-500/15 bg-amber-500/[0.04] p-4 text-sm text-neutral-300">
          <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">
            <BedDouble className="h-3.5 w-3.5" /> Already arranged
          </p>
          {acc.hotel_name && <p className="mt-2">Staying at: {acc.hotel_name} {acc.hotel_address ? `— ${acc.hotel_address}` : ""}</p>}
          {acc.existing_bookings && <p className="mt-1 text-neutral-400">{acc.existing_bookings}</p>}
        </div>
      )}

      {req.message && <p className="mt-4 text-sm italic leading-relaxed text-neutral-400">“{req.message}”</p>}

      <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-amber-500/10 pt-4">
        {(STATUS_ACTIONS[req.status] || STATUS_ACTIONS.new).map((s) => (
          <button
            key={s}
            data-testid={`request-${s}-${req.id.slice(0, 8)}`}
            onClick={() => onStatus(req.id, s)}
            className="border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
          >
            Mark {s}
          </button>
        ))}
        <button
          data-testid={`request-delete-${req.id.slice(0, 8)}`}
          onClick={() => onDelete(req.id)}
          className="ml-auto flex items-center gap-1.5 border border-red-900/50 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-red-400/80 transition-colors hover:border-red-500 hover:text-red-300"
        >
          <Trash2 className="h-3 w-3" /> Delete
        </button>
      </div>
    </motion.div>
  );
};

export default function OwnerPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem("er-owner-token") || "");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [requests, setRequests] = useState(null);
  const [filter, setFilter] = useState("all");
  // on a phone, open the desk on the big two-choice hub once per session
  const [hubOpen, setHubOpen] = useState(() => window.matchMedia("(max-width: 640px)").matches && !sessionStorage.getItem("er-hub-seen"));

  const login = async () => {
    setLoggingIn(true);
    setAuthError("");
    try {
      const res = await axios.post(`${API}/owner/login`, { password });
      localStorage.setItem("er-owner-token", res.data.token);
      localStorage.setItem("er-owner-role", res.data.role || "owner");
      setToken(res.data.token);
      setPassword("");
      // every sign-in lands on the trip builder (phones keep their hub)
      sessionStorage.removeItem("er-landed");
      if (!window.matchMedia("(max-width: 640px)").matches) navigate("/owner/trips");
    } catch {
      setAuthError("That key does not open this door.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem("er-owner-token");
    localStorage.removeItem("er-owner-role");
    sessionStorage.removeItem("er-landed");
    setToken("");
    setRequests(null);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/owner/requests`, { headers: { Authorization: `Bearer ${token}` } });
      setRequests(res.data.requests);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired — please sign in again.");
        logout();
      } else {
        toast.error("Could not load requests.");
      }
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  // signing in always lands on the trip builder — fresh requests arrive as a popup there.
  // Once per session (the REQUESTS link still works after), and phones keep their hub.
  useEffect(() => {
    if (!token || !requests) return;
    if (window.matchMedia("(max-width: 640px)").matches) return;
    if (sessionStorage.getItem("er-landed")) return;
    sessionStorage.setItem("er-landed", "1");
    navigate("/owner/trips", { replace: true });
  }, [token, requests, navigate]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/owner/requests/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update the request.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/owner/requests/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setRequests((rs) => rs.filter((r) => r.id !== id));
      toast.success("Request deleted");
    } catch {
      toast.error("Could not delete the request.");
    }
  };

  if (!token) {
    return (
      <div data-testid="owner-login" className="flex min-h-screen flex-col items-center justify-center bg-[#070709] px-5">
        <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="h-16 w-auto mix-blend-screen" />
        <motion.div
          className="mt-10 w-full max-w-sm border border-amber-500/20 bg-[#0c0c10] p-8"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-amber-300/80">Admin Access</p>
          <h1 className="mt-2 font-serif text-3xl font-light text-neutral-50">The private door.</h1>
          <input
            type="password"
            data-testid="owner-password-input"
            placeholder="Owner password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            className="mt-6 w-full border border-amber-900/40 bg-[#0d0d12] px-4 py-3 text-sm text-amber-100 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400"
          />
          {authError && (
            <p data-testid="owner-login-error" className="mt-3 text-xs text-red-400">{authError}</p>
          )}
          <button
            data-testid="owner-login-submit"
            onClick={login}
            disabled={loggingIn || !password}
            className="mt-5 flex w-full items-center justify-center gap-2 bg-amber-500 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300 disabled:opacity-50"
          >
            {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            Unlock
          </button>
          <Link to="/" data-testid="owner-back-home" className="mt-5 block text-center text-[10px] uppercase tracking-[0.25em] text-neutral-500 transition-colors hover:text-amber-200">
            Return to the site
          </Link>
        </motion.div>
      </div>
    );
  }

  const filtered = (requests || []).filter((r) => filter === "all" || r.status === filter);
  const counts = (requests || []).reduce((m, r) => ({ ...m, [r.status]: (m[r.status] || 0) + 1 }), {});

  if (hubOpen) {
    return (
      <div data-testid="owner-dashboard" className="min-h-screen bg-[#070709] text-neutral-100">
        <ChangesPopup token={token} />
        <OwnerHub
          onClients={() => {
            sessionStorage.setItem("er-hub-seen", "1");
            navigate("/owner/trips");
          }}
          onNew={() => {
            sessionStorage.setItem("er-hub-seen", "1");
            navigate("/owner/trips?new=1");
          }}
        />
      </div>
    );
  }

  return (
    <div data-testid="owner-dashboard" className="min-h-screen bg-[#070709] text-neutral-100">
      <ChangesPopup token={token} />
      <header className="sticky top-0 z-40 border-b border-amber-500/15 bg-[#0c0c10]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="flex items-center gap-4">
            <img src="/assets/logo.png" alt="" className="h-10 w-auto mix-blend-screen" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Concierge Desk</p>
              <h1 className="font-serif text-xl font-light text-neutral-50">Trip Requests</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/owner/trips"
              data-testid="owner-trips-link"
              className="flex items-center gap-2 border border-amber-500/50 bg-amber-500/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
            >
              Trip Builder
            </Link>
            <Link
              to="/owner/debts"
              data-testid="owner-debts-link"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              Debt List
            </Link>
            <Link
              to="/owner/catalogue"
              data-testid="owner-catalogue-link"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              Webpage Layout
            </Link>
            <button
              data-testid="owner-refresh-button"
              onClick={load}
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              data-testid="owner-logout-button"
              onClick={logout}
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-red-400 hover:text-red-300"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex flex-wrap gap-2">
          {["all", "new", "contacted", "confirmed", "archived"].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setFilter(s)}
              className={`border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.25em] transition-colors ${
                filter === s
                  ? "border-amber-400 bg-amber-500 text-black"
                  : "border-neutral-700 text-neutral-400 hover:border-amber-500/50 hover:text-amber-200"
              }`}
            >
              {s} {s === "all" ? `(${requests?.length ?? 0})` : counts[s] ? `(${counts[s]})` : ""}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-5">
          {requests === null ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="owner-empty-state" className="border border-dashed border-neutral-700 py-20 text-center">
              <CalendarDays className="mx-auto h-8 w-8 text-neutral-600" />
              <p className="mt-4 font-serif text-2xl font-light text-neutral-400">No requests here yet.</p>
              <p className="mt-2 text-sm text-neutral-600">New client requests will appear the moment they arrive.</p>
            </div>
          ) : (
            filtered.map((r) => <RequestCard key={r.id} req={r} onStatus={setStatus} onDelete={remove} />)
          )}
        </div>
      </main>
    </div>
  );
}
